// Тости: ToastProvider (обгортка з вьюпортом) і useToast(). Сховище — src/lib/toast.ts (можна викликати й поза React).
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { dismissToast, subscribeToasts, toast, type ToastItem, type Toaster } from '@/lib/toast';

const icons = { info: Info, success: CheckCircle2, warning: AlertTriangle, danger: XCircle };
const iconTones = { info: 'text-info', success: 'text-success', warning: 'text-warning', danger: 'text-danger' };
const bars = { info: 'bg-info', success: 'bg-success', warning: 'bg-warning', danger: 'bg-danger' };

/** Контейнер тостів (у ToastProvider). */
export function ToastViewport() {
  const [items, setItems] = useState<ToastItem[]>([]);
  useEffect(() => subscribeToasts(setItems), []);
  return (
    <div
      aria-live="polite"
      aria-relevant="additions"
      className="pointer-events-none fixed inset-x-3 bottom-3 z-[70] flex flex-col items-end gap-2 sm:inset-x-auto sm:right-4 sm:bottom-4 sm:w-[380px]"
    >
      {items.map((t) => {
        const Icon = icons[t.tone];
        return (
          <div
            key={t.id}
            role={t.tone === 'danger' ? 'alert' : 'status'}
            className="pointer-events-auto relative flex w-full items-start gap-3 overflow-hidden rounded-field border border-border bg-surface py-3 pr-3 pl-4 shadow-popover animate-toast-in"
          >
            <span className={cn('absolute inset-y-0 left-0 w-1', bars[t.tone])} aria-hidden />
            <Icon className={cn('mt-0.5 size-[18px] shrink-0', iconTones[t.tone])} aria-hidden />
            <div className="min-w-0 flex-1 text-[14px]">
              <p className="font-semibold break-words whitespace-pre-line">{t.title}</p>
              {t.description ? <p className="mt-0.5 text-[13px] break-words whitespace-pre-line text-muted">{t.description}</p> : null}
              {t.action ? (
                <button
                  type="button"
                  className="mt-1.5 text-[13px] font-semibold text-primary hover:text-primary-hover"
                  onClick={() => {
                    t.action?.onClick();
                    dismissToast(t.id);
                  }}
                >
                  {t.action.label}
                </button>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => dismissToast(t.id)}
              className="flex size-7 shrink-0 items-center justify-center rounded-md text-faint hover:bg-surface-2 hover:text-fg"
              aria-label="Закрити повідомлення"
            >
              <X className="size-3.5" />
            </button>
          </div>
        );
      })}
    </div>
  );
}

/** Провайдер тостів: рендерить дітей і вьюпорт. */
export function ToastProvider({ children }: { children: ReactNode }) {
  return (
    <>
      {children}
      <ToastViewport />
    </>
  );
}

/** const t = useToast(); t.success('Збережено'). */
export function useToast(): Toaster {
  return toast;
}
